import React, { useContext, useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Users, Send, LayoutDashboard, LogOut, Menu, X, MessageCircle, UserCog, Bell, MessageSquare, Clock, Gamepad2 } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Layout = () => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Load collapsed state from localStorage or default to false
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });

  // Save collapsed state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', isCollapsed.toString());
  }, [isCollapsed]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const menuItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'recepcao', 'cognicao', 'user'] },
    { path: '/dashboard-game', icon: Gamepad2, label: 'Dashboard Game', roles: ['admin', 'recepcao', 'cognicao', 'user'] },
    { path: '/contacts', icon: Users, label: 'Contatos', roles: ['admin', 'recepcao', 'cognicao', 'user'] },
    { path: '/campaigns', icon: Send, label: 'Campanhas', roles: ['admin', 'user'] },
    { path: '/responses', icon: MessageCircle, label: 'Respostas', roles: ['admin', 'user'] },
    { path: '/espera', icon: Clock, label: 'Espera', roles: ['admin', 'recepcao'] },
    { path: '/evolucao', icon: MessageCircle, label: 'Evolução', roles: ['admin', 'cognicao'] },
    { path: '/whatsapp', icon: MessageSquare, label: 'WhatsApp', roles: ['admin', 'user'] },
    { path: '/users', icon: UserCog, label: 'Usuários', roles: ['admin'] },
  ].filter(item => {
    const userRole = user?.role || 'user';
    return item.roles.includes(userRole);
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          data-testid="mobile-menu-toggle"
          variant="outline"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="bg-white shadow-lg"
        >
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {/* Top Header Bar */}
      <header 
        className={`fixed top-0 right-0 h-20 bg-white border-b border-gray-200 z-30 transition-all duration-300 ${
          isCollapsed ? 'lg:left-20' : 'lg:left-[276px]'
        }`}
      >
        <div className="h-full px-6 flex items-center justify-between">
          {/* Page Title or Breadcrumb */}
          <div className="flex-1">
            <h1 className="text-xl font-semibold text-gray-900">
              {menuItems.find(item => location.pathname === item.path || location.pathname.startsWith(item.path + '/'))?.label || 'WhaCamp'}
            </h1>
          </div>

          {/* Header Actions */}
          <div className="flex items-center gap-4">
            {/* Notifications */}
            <Button
              variant="ghost"
              size="icon"
              className="relative hover:bg-gray-100"
              title="Notificações"
            >
              <Bell className="w-5 h-5 text-gray-600" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </Button>

            {/* User Dropdown Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 pl-4 border-l border-gray-200 hover:opacity-80 transition-opacity cursor-pointer focus:outline-none">
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                    <p className="text-xs text-gray-600">{user?.email}</p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-700 to-green-400 flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Minha Conta</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="px-2 py-2">
                  <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                  <p className="text-xs text-gray-600">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-red-600 cursor-pointer focus:text-red-700 focus:bg-red-50"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Sidebar - width increased by 20px: w-64 (256px) -> w-[276px] */}
      <aside
        className={`fixed top-0 left-0 h-screen bg-white border-r border-gray-200 z-40 transform transition-all duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 ${
          isCollapsed ? 'w-20' : 'w-[276px]'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo - Clickable to toggle collapse */}
          <div className={`mb-8 p-4 border-b border-gray-100 ${isCollapsed ? 'px-4' : ''}`}>
            <button
              onClick={toggleCollapse}
              className={`flex items-center justify-center gap-2 w-full hover:opacity-80 transition-opacity ${
                isCollapsed ? 'justify-center' : ''
              }`}
              title={isCollapsed ? 'Expandir menu' : 'Colapsar menu'}
            >
              {isCollapsed ? (
                <img 
                  src="https://customer-assets.emergentagent.com/job_campaign-dash-6/artifacts/lxy809hm_file.enc"
                  alt="Espaço Longevus"
                  className="h-12 w-12 object-contain"
                />
              ) : (
                <img 
                  src="https://customer-assets.emergentagent.com/job_campaign-dash-6/artifacts/hfkfcyu3_LOGO%20COR%201.png"
                  alt="Espaço Longevus"
                  className="h-12 w-auto object-contain"
                />
              )}
            </button>
          </div>

          {/* Navigation */}
          <nav className={`flex-1 space-y-2 ${isCollapsed ? 'px-2' : 'px-4'}`}>
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
              return (
                <div key={item.path} className="relative group">
                  <Link
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${
                      isActive
                        ? 'bg-gradient-to-r from-emerald-700 to-green-400 text-white shadow-md'
                        : 'text-gray-700 hover:bg-gray-100'
                    } ${isCollapsed ? 'justify-center' : ''}`}
                    title={isCollapsed ? item.label : ''}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {!isCollapsed && <span>{item.label}</span>}
                  </Link>
                  
                  {/* Tooltip on hover when collapsed */}
                  {isCollapsed && (
                    <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50 pointer-events-none">
                      {item.label}
                      <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Main content - adjusted margin-left and top padding for header */}
      <main 
        className={`min-h-screen pt-16 transition-all duration-300 ${
          isCollapsed ? 'lg:ml-20' : 'lg:ml-[276px]'
        }`}
      >
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
